import { addDecimals, compareDecimals, isZeroDecimal, multiplyDecimals } from '@/src/lib/hospitality/decimal';
import type { MenuItem, MenuModifierGroup, Orderability, RestaurantSite } from '@/src/lib/hospitality/wire';
import type { Cart, CartLine, DinerFulfilmentType } from '@/src/lib/hospitality/orderWire';

/**
 * Every decision `RestaurantOrder` makes, as functions with no React in them.
 *
 * <p>The rules that matter here — a required modifier group with nothing chosen,
 * a dish 86'd while the diner browsed, a collection slot before the kitchen's
 * lead time, a delivery address the shipping engine returned no option for —
 * are the ones that must not be re-derived by eye in a component. They are
 * pure, exported, and tested in `model.test.ts`.
 *
 * <p>No money is a `number` anywhere below, and no quantity is either:
 * `CartLine.quantity` is scale 3 and a menu item can be sold by weight.
 * Arithmetic goes through `lib/hospitality/decimal.ts`, which sums `bigint`
 * minor units.
 */

export interface ModifierChoice {
  slotKey: string;
  productId: number;
  /** Decimal string. */
  quantity: string;
}

export type GroupIssue = 'required' | 'below-min' | 'above-max' | 'unorderable';

/** The chosen quantity across one group, as a decimal string. */
export function groupQuantity(group: MenuModifierGroup, choices: readonly ModifierChoice[]): string {
  return addDecimals(choices.filter((c) => c.slotKey === group.slotKey).map((c) => c.quantity));
}

/**
 * What is wrong with one group's choices, or `null`.
 *
 * <p>`required` blocks submission — that is the brief's first acceptance line.
 * The quantity bounds apply only when `quantityConfigurable` is true; when it
 * is false the slot takes exactly one product (a `KitComponent` slot does), so
 * choosing two is `above-max` regardless of what `maxQuantity` says.
 */
export function groupIssue(
  group: MenuModifierGroup,
  choices: readonly ModifierChoice[],
): GroupIssue | null {
  const mine = choices.filter((c) => c.slotKey === group.slotKey);
  if (mine.some((c) => group.options.find((o) => o.productId === c.productId)?.orderable === false)) {
    return 'unorderable';
  }
  const total = groupQuantity(group, choices);

  if (!group.quantityConfigurable) {
    if (mine.length > 1) return 'above-max';
    if (group.required && mine.length === 0) return 'required';
    return null;
  }

  if (group.required && isZeroDecimal(total)) return 'required';
  if (isZeroDecimal(total) && !group.required) return null;
  if (group.minQuantity && compareDecimals(total, group.minQuantity) < 0) return 'below-min';
  if (group.maxQuantity && compareDecimals(total, group.maxQuantity) > 0) return 'above-max';
  return null;
}

/** Every group's issue, keyed by `slotKey`. Empty means the dialog may submit. */
export function itemIssues(
  item: MenuItem,
  choices: readonly ModifierChoice[],
): Record<string, GroupIssue> {
  const issues: Record<string, GroupIssue> = {};
  for (const group of item.modifierGroups) {
    const issue = groupIssue(group, choices);
    if (issue) issues[group.slotKey] = issue;
  }
  return issues;
}

/** Whether the "add to order" button may be enabled. */
export function canAddItem(item: MenuItem, choices: readonly ModifierChoice[]): boolean {
  return item.orderable && Object.keys(itemIssues(item, choices)).length === 0;
}

/**
 * The quantity one option contributes when the group is not configurable: the
 * group's `defaultQuantity`, else exactly one. Never a number chosen here.
 */
export function fixedChoiceQuantity(group: MenuModifierGroup): string {
  return group.defaultQuantity && !isZeroDecimal(group.defaultQuantity)
    ? group.defaultQuantity
    : '1';
}

/**
 * The price to SHOW for a configured item before it exists server-side: the
 * dish plus each chosen option's price times its quantity.
 *
 * <p>Display only. The authoritative figure is whatever `CartDto` comes back
 * with, priced by the engine against the channel's rules — this is what the
 * dialog puts on its button so the diner is not surprised.
 */
export function previewUnitPrice(item: MenuItem, choices: readonly ModifierChoice[]): string | null {
  if (!item.price) return null;
  const parts: (string | null)[] = [item.price];
  for (const choice of choices) {
    const group = item.modifierGroups.find((g) => g.slotKey === choice.slotKey);
    const option = group?.options.find((o) => o.productId === choice.productId);
    if (!option?.price) continue;
    parts.push(multiplyDecimals(option.price, choice.quantity));
  }
  return addDecimals(parts);
}

/**
 * Cart lines as a tree: `MODIFIER` rows under the line they belong to.
 *
 * <p>`CartLine.parentLine` is the only thing that says which dish an option
 * belongs to, and a flat list of "Fries", "Extra cheese", "Fries" is unreadable
 * and unremovable. Orphans — a modifier whose parent is not in the cart — are
 * kept as their own top-level row rather than dropped: silently hiding a line a
 * diner will be charged for is worse than showing an odd one.
 */
export interface CartNode {
  line: CartLine;
  modifiers: CartLine[];
}

export function cartTree(cart: Cart | null): CartNode[] {
  if (!cart) return [];
  const byId = new Map<number, CartNode>();
  const roots: CartNode[] = [];
  for (const line of cart.lines) {
    if (line.lineType !== 'MODIFIER') {
      const node: CartNode = { line, modifiers: [] };
      byId.set(line.id, node);
      roots.push(node);
    }
  }
  for (const line of cart.lines) {
    if (line.lineType !== 'MODIFIER') continue;
    const parent = line.parentLineId == null ? undefined : byId.get(line.parentLineId);
    if (parent) parent.modifiers.push(line);
    else roots.push({ line, modifiers: [] });
  }
  return roots;
}

/**
 * The cut-off re-check: which cart lines have become unorderable.
 *
 * <p>Keyed by `CartLine.id`, so the block flags the line IN PLACE with its
 * reason. Nothing is removed here and nothing may be: the brief is explicit
 * that a line that went unorderable while the customer browsed must be removed
 * or substituted by the diner, never silently dropped.
 */
export function unorderableLines(
  cart: Cart | null,
  results: ReadonlyMap<number, Orderability | null>,
): Map<number, Orderability> {
  const flagged = new Map<number, Orderability>();
  for (const line of cart?.lines ?? []) {
    if (line.productId == null) continue;
    const result = results.get(line.productId);
    if (result && !result.orderable) flagged.set(line.id, result);
  }
  return flagged;
}

/** The distinct product ids a cut-off re-check has to ask about — modifiers
 *  included, because an option can be 86'd on its own. */
export function productIdsToRecheck(cart: Cart | null): number[] {
  const ids = new Set<number>();
  for (const line of cart?.lines ?? []) {
    if (line.productId != null) ids.add(line.productId);
  }
  return [...ids];
}

/** A `DELIVERY` cart with no shipping option is an address outside the
 *  delivery area — `ShippingQuoteService`'s answer, and the only delivery-area
 *  test this app makes. */
export function isOutOfDeliveryArea(cart: Cart | null, fulfilment: DinerFulfilmentType): boolean {
  return fulfilment === 'DELIVERY' && !!cart && cart.shippingOptions.length === 0;
}

/** True when any line in the cart is priced tax-exclusive, so the panel shows
 *  "+ tax". Read per line from the API — never from a channel's name and never
 *  assumed because the site is a restaurant. */
export function hasTaxExclusiveLine(cart: Cart | null): boolean {
  return (cart?.lines ?? []).some((line) => !line.taxInclusive);
}

// ---------------------------------------------------------------------------
// The requested-time picker
// ---------------------------------------------------------------------------

const DAY_KEYS = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
] as const;

/** The weekday of an ISO calendar date. Computed in UTC deliberately: the value
 *  is a calendar date with no instant behind it, and running it through a real
 *  zone is what turns Monday into Sunday for half the world. */
export function weekdayOf(isoDate: string): string | null {
  const time = Date.parse(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(time)) return null;
  return DAY_KEYS[new Date(time).getUTCDay()];
}

export function isClosedDate(site: RestaurantSite, isoDate: string): boolean {
  return site.closures.some((closure) => closure.closedOn === isoDate);
}

/** Minutes past midnight for `HH:mm[:ss]`, or null. */
export function minutesOfDay(clock: string | null): number | null {
  if (!clock) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(clock.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  return hours * 60 + minutes;
}

/**
 * The UTC offset of a named zone at an instant, in minutes.
 *
 * <p>Read off `Intl` rather than computed: the alternative is a DST table, and
 * this app hard-codes no timezone, no country and no offset. `longOffset`
 * yields `GMT+03:00`, or plain `GMT` at zero.
 */
function zoneOffsetMinutes(instant: number, timeZone: string): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date(instant));
    const name = parts.find((part) => part.type === 'timeZoneName')?.value ?? 'GMT';
    const match = /GMT([+-])(\d{2}):?(\d{2})?/.exec(name);
    if (!match) return 0;
    const sign = match[1] === '-' ? -1 : 1;
    return sign * (Number(match[2]) * 60 + Number(match[3] ?? '0'));
  } catch {
    return 0;
  }
}

/**
 * A local date and wall-clock time in the restaurant's zone, as an ISO instant.
 *
 * <p>Applied twice on purpose. The first pass uses the offset at the naive
 * instant, which is the wrong offset for the hour either side of a DST change;
 * the second uses the offset at the instant the first pass produced, which
 * converges. This is the standard fix and it is why the function exists rather
 * than a `new Date(\`${date}T${time}\`)` that would silently use the SERVER's
 * zone — a promised collection time an hour out is a cold dinner.
 */
export function zonedInstant(isoDate: string, minutes: number, timeZone: string): string | null {
  const midnight = Date.parse(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(midnight)) return null;
  const naive = midnight + minutes * 60_000;
  let instant = naive - zoneOffsetMinutes(naive, timeZone) * 60_000;
  instant = naive - zoneOffsetMinutes(instant, timeZone) * 60_000;
  return new Date(instant).toISOString();
}

export interface TimeSlot {
  /** ISO instant, what `requestedDeliveryAt` is sent as. */
  at: string;
  /** `HH:mm` in the restaurant's zone, what the diner reads. */
  label: string;
}

export interface SlotQueryInput {
  site: RestaurantSite;
  /** ISO calendar date the diner picked. */
  isoDate: string;
  /** Now, as an epoch millisecond value. Injected so the rule is testable. */
  nowMs: number;
  /** Defaults to the brief's 15 minutes. */
  stepMinutes?: number;
}

/**
 * Collection/takeaway times for one date.
 *
 * <p>Inside the site's `hours` for that weekday, minus `closures`, at
 * 15-minute steps, starting no earlier than `orderLeadMinutes` after now.
 *
 * <p><strong>`orderLeadMinutes === null` returns nothing at all.</strong> The
 * value comes from `Channel.config["order.leadMinutes"]` and null means the
 * operator has not set one; a picker that substitutes fifteen minutes of its
 * own is promising a kitchen something nobody agreed to. The block says "call
 * us" instead.
 */
export function collectionSlots({ site, isoDate, nowMs, stepMinutes = 15 }: SlotQueryInput): TimeSlot[] {
  if (site.orderLeadMinutes == null) return [];
  if (isClosedDate(site, isoDate)) return [];
  const weekday = weekdayOf(isoDate);
  if (!weekday) return [];

  const earliest = nowMs + site.orderLeadMinutes * 60_000;
  const slots: TimeSlot[] = [];
  const seen = new Set<string>();

  for (const window of site.hours) {
    if (window.dayOfWeek !== weekday) continue;
    const opens = minutesOfDay(window.opensAt);
    const closes = minutesOfDay(window.closesAt);
    if (opens == null || closes == null || closes <= opens) continue;

    const first = Math.ceil(opens / stepMinutes) * stepMinutes;
    for (let minutes = first; minutes <= closes; minutes += stepMinutes) {
      const at = zonedInstant(isoDate, minutes, site.timeZone);
      if (!at || seen.has(at)) continue;
      if (Date.parse(at) < earliest) continue;
      seen.add(at);
      slots.push({ at, label: clockLabel(minutes) });
    }
  }
  return slots.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
}

function clockLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60) % 24;
  return `${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/** An instant as `HH:mm` on a date, in the restaurant's own zone — how a
 *  promised time is shown back on the confirmation. Never the visitor's zone:
 *  the kitchen's clock is the one that matters. */
export function formatInZone(instant: string, timeZone: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      timeZone,
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(instant));
  } catch {
    return instant;
  }
}
